/**
 * Physical Process Edit Route
 *
 * Edit an existing physical process using PhysicalProcessForm component
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { usePhysicalProcess } from '../../graphql/queries';
import { useApolloClient } from '@apollo/client/react';
import { PhysicalProcessForm } from '../../components/physical-processes';
import { LoadingSpinner, EmptyState } from '../../components/shared';
import {
  updatePhysicalProcess,
  type UpdatePhysicalProcessInput,
  ApiError,
} from '../../lib/apiClient';
import type { CreatePhysicalProcessFormData } from '../../components/forms';
import './edit.css';

export const Route = createFileRoute('/physical-processes/$physicalProcessId/edit')({
  component: PhysicalProcessEditPage,
});

function PhysicalProcessEditPage(): JSX.Element {
  const BannerElement = 'forge-banner' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const { physicalProcessId } = Route.useParams();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch physical process data
  const { data, loading, error: fetchError } = usePhysicalProcess(physicalProcessId);
  const physicalProcess = data?.physicalProcess;

  if (loading) {
    return (
      <div className="physical-process-edit-page">
        <LoadingSpinner centered message="Loading physical process..." />
      </div>
    );
  }

  if (fetchError || !physicalProcess) {
    return (
      <div className="physical-process-edit-page">
        <EmptyState
          icon="error"
          title="Failed to load physical process"
          description={fetchError?.message || 'Physical process not found'}
          actionLabel="Back to Physical Processes"
          onAction={() =>
            navigate({ to: '/physical-processes', search: { page: 1, pageSize: 25 } })
          }
        />
      </div>
    );
  }

  const handleSubmit = async (formData: CreatePhysicalProcessFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Build update input
      const updates: UpdatePhysicalProcessInput = {
        name: formData.name,
        description: formData.description || undefined,
        processType: formData.processType,
        processTier: formData.processTier,
        processOwnerId: formData.processOwnerId,
        owningBusinessUnitId: formData.owningBusinessUnitId,
        automationLevel: formData.automationLevel || undefined,
        status: formData.status || undefined,
        estimatedDuration: formData.estimatedDuration || undefined,
        volumePerPeriod: formData.volumePerPeriod || undefined,
        inputDescription: formData.inputDescription || undefined,
        outputDescription: formData.outputDescription || undefined,
        toolingDescription: formData.toolingDescription || undefined,
        tags: formData.tags && formData.tags.length > 0 ? formData.tags : undefined,
        notes: formData.notes || undefined,
      };

      await updatePhysicalProcess(physicalProcessId, updates);

      // Refetch queries to update cache
      await apolloClient.refetchQueries({
        include: ['GetPhysicalProcess', 'ListPhysicalProcesses'],
      });

      // Navigate back to physical process detail page
      navigate({ to: `/physical-processes/${physicalProcessId}` as string });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    navigate({ to: `/physical-processes/${physicalProcessId}` as string });
  };

  // Map physical process data to form default values
  const defaultValues: Partial<CreatePhysicalProcessFormData> = {
    name: physicalProcess.name,
    description: physicalProcess.description || undefined,
    processType: physicalProcess.processType,
    processTier: physicalProcess.processTier,
    processOwnerId: physicalProcess.processOwnerId,
    owningBusinessUnitId: physicalProcess.owningBusinessUnitId,
    automationLevel: (physicalProcess.automationLevel || undefined) as
      'manual' | 'semi-automated' | 'automated' | 'autonomous' | undefined,
    status: physicalProcess.status || undefined,
    estimatedDuration: physicalProcess.estimatedDuration || undefined,
    volumePerPeriod: physicalProcess.volumePerPeriod || undefined,
    inputDescription: physicalProcess.inputDescription || undefined,
    outputDescription: physicalProcess.outputDescription || undefined,
    toolingDescription: physicalProcess.toolingDescription || undefined,
    tags: physicalProcess.tags || [],
    notes: physicalProcess.notes || undefined,
  };

  return (
    <div className="physical-process-edit-page">
      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Edit Physical Process</h1>
        <p className="forge-typography--body2 page-header-subtitle">
          Update physical process information
        </p>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <PhysicalProcessForm
        mode="edit"
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
